/*
  <ilura-video> — wraps an autoplaying background clip and takes it away from
  anyone who has asked not to see motion.

  A looping, muted, controlless video is decoration. `prefers-reduced-motion`
  is a request not to be shown that, and unlike a CSS transition it cannot be
  switched off from a stylesheet — playback is a property of the element, so it
  needs a line of script. Without this the setting is simply ignored here,
  which is the one place in this theme where that would be true.

  Paused rather than hidden: a paused video shows its poster frame, so the
  layout is unchanged and the visitor still sees the subject, just still. The
  same reasoning as everywhere else in this theme — reduced motion removes the
  movement, never the content.

  The query is watched rather than read once, so toggling the OS setting takes
  effect on the open page rather than at the next reload.

  A custom element rather than a DOMContentLoaded pass because
  connectedCallback re-fires when the theme editor re-renders a section, and
  disconnectedCallback gives somewhere to drop the listener. A one-shot pass
  would leave every editor edit bound to a detached node.
*/
if (!customElements.get('ilura-video')) {
  const REDUCED = '(prefers-reduced-motion: reduce)';

  class IluraVideo extends HTMLElement {
    connectedCallback() {
      this.query = window.matchMedia(REDUCED);
      this.onChange = () => this.apply();

      /* addEventListener on a MediaQueryList is the modern form; Safari below
         14 only has addListener. Both are guarded so an older browser gets the
         initial state applied rather than a thrown error that would also stop
         the video ever being paused. */
      if (this.query.addEventListener) {
        this.query.addEventListener('change', this.onChange);
      } else if (this.query.addListener) {
        this.query.addListener(this.onChange);
      }

      this.apply();
    }

    disconnectedCallback() {
      if (!this.query) return;

      if (this.query.removeEventListener) {
        this.query.removeEventListener('change', this.onChange);
      } else if (this.query.removeListener) {
        this.query.removeListener(this.onChange);
      }
    }

    apply() {
      const video = this.querySelector('video');
      if (!video) return;

      if (this.query.matches) {
        video.pause();
        /* Back to the first frame, which is the poster. Leaving it paused
           mid-clip would show an arbitrary frame that the poster was chosen
           in preference to. */
        try {
          video.currentTime = 0;
        } catch (e) {
          /* Seeking before metadata loads throws in some browsers. The poster
             is showing anyway until then, so there is nothing to correct. */
        }
        video.removeAttribute('autoplay');
        return;
      }

      /* play() rejects on its own when the browser blocks autoplay — a power
         saving mode, a data saver, a tab that has never been interacted with.
         That is the browser's call to make, and the poster frame is a complete
         fallback, so the rejection is swallowed rather than reported. */
      const played = video.play();
      if (played && typeof played.catch === 'function') played.catch(() => {});
    }
  }

  customElements.define('ilura-video', IluraVideo);
}
